import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Checkbox,
  FormControlLabel,
  Divider,
  Paper,
  Chip,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import api from '../../services/api';

function AIGuidanceModal({ open, onClose, transaction, receiptFile, onComplete }) {
  // Step: 'analyzing' | 'review_analysis' | 'review_fields'
  const [step, setStep] = useState('analyzing');

  // Update vendor registry option
  const [updateRegistry, setUpdateRegistry] = useState(false);
  const hasLinkedVendor = !!(transaction?.vendor_id || transaction?.vendor_slug);

  // Receipt analysis from vision API
  const [receiptAnalysis, setReceiptAnalysis] = useState('');
  const [analysisError, setAnalysisError] = useState('');

  // User optional inputs
  const [businessPct, setBusinessPct] = useState('');
  const [briefGuidance, setBriefGuidance] = useState('');

  // AI-generated fields
  const [generatedFields, setGeneratedFields] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState('');

  // Override toggles
  const [overrides, setOverrides] = useState({});

  // Reset when modal opens — watch both open and receiptFile
  useEffect(() => {
    if (open && receiptFile) {
      setStep('analyzing');
      setReceiptAnalysis('');
      setAnalysisError('');
      setBusinessPct('');
      setBriefGuidance('');
      setGeneratedFields(null);
      setGenerateError('');
      setOverrides({});
      setUpdateRegistry(false);
      analyzeReceipt();
    }
  }, [open, receiptFile]);

  const analyzeReceipt = async () => {
    if (!receiptFile) return;

    try {
      // Send just the receipt for vision analysis — no field generation yet
      const formData = new FormData();
      formData.append('receipt', receiptFile);
      formData.append('description', transaction?.description || '');
      formData.append('amount', (transaction?.amount || 0).toString());
      formData.append('date', transaction?.date || '');
      formData.append('business_purpose_brief', '');
      // Don't send business_pct — let analysis be neutral

      const res = await api.post('/compliance/ai-complete', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const data = res.data.data;
      setReceiptAnalysis(data.receipt_analysis || '');
      // Store the initial AI suggestions but don't show them yet
      setGeneratedFields(data);
      setStep('review_analysis');
    } catch (err) {
      setAnalysisError(err.response?.data?.msg || 'Receipt analysis failed');
      setStep('review_analysis');
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setGenerateError('');

    try {
      const formData = new FormData();
      formData.append('description', transaction?.description || '');
      formData.append('amount', (transaction?.amount || 0).toString());
      formData.append('date', transaction?.date || '');
      formData.append('business_purpose_brief', briefGuidance);
      if (businessPct !== '') {
        formData.append('business_pct', businessPct.toString());
      }
      // Pass cached receipt analysis — no need to re-upload
      if (receiptAnalysis) {
        formData.append('receipt_analysis', receiptAnalysis);
      }

      const res = await api.post('/compliance/ai-complete', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setGeneratedFields(res.data.data);
      setStep('review_fields');
    } catch (err) {
      setGenerateError(err.response?.data?.msg || 'Field generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleConfirm = () => {
    if (!generatedFields) return;

    // Apply any overrides
    const finalFields = { ...generatedFields };
    Object.entries(overrides).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        finalFields[key] = value;
      }
    });

    // Include receipt analysis for caching
    finalFields.receipt_analysis = receiptAnalysis;
    finalFields._receiptFile = receiptFile;
    finalFields._updateRegistry = updateRegistry;

    onComplete(finalFields);
    onClose();
  };

  const toggleOverride = (fieldName) => {
    if (overrides.hasOwnProperty(fieldName)) {
      const newOverrides = { ...overrides };
      delete newOverrides[fieldName];
      setOverrides(newOverrides);
    } else {
      setOverrides({ ...overrides, [fieldName]: generatedFields?.[fieldName] || '' });
    }
  };

  const updateOverride = (fieldName, value) => {
    setOverrides({ ...overrides, [fieldName]: value });
  };

  const renderField = (label, fieldName, value, multiline = false) => {
    const isOverridden = overrides.hasOwnProperty(fieldName);
    return (
      <Box sx={{ mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isOverridden}
                onChange={() => toggleOverride(fieldName)}
                size="small"
              />
            }
            label={<Typography variant="caption" color="text.secondary">Edit</Typography>}
            sx={{ m: 0 }}
          />
          <Typography variant="subtitle2">{label}</Typography>
        </Box>
        {isOverridden ? (
          <TextField
            value={overrides[fieldName]}
            onChange={(e) => updateOverride(fieldName, fieldName === 'business_pct' ? e.target.value : e.target.value)}
            fullWidth
            size="small"
            multiline={multiline}
            rows={multiline ? 3 : 1}
            type={fieldName === 'business_pct' ? 'number' : 'text'}
            inputProps={fieldName === 'business_pct' ? { min: 0, max: 100 } : {}}
          />
        ) : (
          <Paper variant="outlined" sx={{ p: 1, backgroundColor: 'grey.50' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {value || '—'}
            </Typography>
          </Paper>
        )}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AutoFixHighIcon color="primary" />
        AI Receipt Analysis
      </DialogTitle>
      <DialogContent>

        {/* Step 1: Analyzing */}
        {step === 'analyzing' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
            <CircularProgress size={48} />
            <Typography variant="body1">Analyzing receipt...</Typography>
            <Typography variant="caption" color="text.secondary">
              {receiptFile?.name}
            </Typography>
          </Box>
        )}

        {/* Step 2: Review Analysis + Optional Inputs */}
        {step === 'review_analysis' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {analysisError ? (
              <Alert severity="error">{analysisError}</Alert>
            ) : (
              <>
                <Alert severity="success" icon={<CheckCircleIcon />}>
                  Receipt analyzed successfully
                </Alert>
                <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f8f9fa', maxHeight: 200, overflow: 'auto' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>What AI found:</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {receiptAnalysis || 'No text extracted'}
                  </Typography>
                </Paper>
              </>
            )}

            <Divider />

            <Typography variant="subtitle2">Optional guidance for AI:</Typography>

            <TextField
              label="Business Use %"
              type="number"
              value={businessPct}
              onChange={(e) => setBusinessPct(e.target.value)}
              size="small"
              sx={{ width: 150 }}
              inputProps={{ min: 0, max: 100 }}
              helperText="Leave blank and AI will estimate"
            />

            <TextField
              label="Additional context (optional)"
              value={briefGuidance}
              onChange={(e) => setBriefGuidance(e.target.value)}
              fullWidth
              size="small"
              multiline
              rows={2}
              placeholder="e.g., this is a dedicated office workstation setup"
            />

            {generateError && <Alert severity="error">{generateError}</Alert>}
          </Box>
        )}

        {/* Step 3: Review Generated Fields */}
        {step === 'review_fields' && generatedFields && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
            <Alert severity="info" sx={{ mb: 1 }}>
              Review the AI-generated fields below. Check the "Edit" box next to any field to override it.
            </Alert>

            {renderField('Vendor Name', 'vendor_name', generatedFields.vendor_name)}
            {renderField('Category', 'category', generatedFields.category)}
            {renderField('Business Use %', 'business_pct', generatedFields.business_pct)}
            {renderField('Rationale', 'pct_rationale', generatedFields.pct_rationale)}
            {renderField('Brief Purpose', 'business_purpose_brief', generatedFields.business_purpose_brief)}
            {renderField('Formal Purpose (IRS)', 'business_purpose_formal', generatedFields.business_purpose_formal, true)}

            {hasLinkedVendor && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={updateRegistry}
                    onChange={(e) => setUpdateRegistry(e.target.checked)}
                  />
                }
                label={`Update "${transaction.vendor_name}" vendor registry for future months`}
                sx={{ mt: 1 }}
              />
            )}
          </Box>
        )}

      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>

        {step === 'review_analysis' && (
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={generating || (!!analysisError && !receiptAnalysis)}
            startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <AutoFixHighIcon />}
          >
            {generating ? 'Generating...' : 'Generate Fields'}
          </Button>
        )}

        {step === 'review_fields' && (
          <>
            <Button onClick={() => setStep('review_analysis')}>
              Back
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleConfirm}
            >
              Confirm & Apply
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default AIGuidanceModal;
